import DOMPurify from 'dompurify';

interface URLValidationResult {
  isValid: boolean;
  sanitizedUrl?: string;
  reason?: string;
}

interface SecurityViolation {
  type: 'blocked_url' | 'blocked_protocol' | 'invalid_url' | 'blocked_domain';
  url: string;
  reason: string;
  timestamp: Date;
  fileId?: number;
  enquiryId?: number;
}

/**
 * Configure DOMPurify for PDF annotation security
 * Similar to HTML Viewer implementation but tailored for PDF links
 */
const configureDOMPurifyForPDF = () => {
  // Create a custom DOMPurify configuration for PDF annotations
  const config = {
    ALLOWED_TAGS: ['a'],
    ALLOWED_ATTR: ['href', 'target', 'rel'],
    ALLOWED_URI_REGEXP: /^https:\/\/|^#/i, // Only HTTPS and internal anchors
    FORBID_TAGS: ['script', 'object', 'embed', 'iframe'],
    FORBID_ATTR: ['onclick', 'onload', 'onerror', 'onmouseover'],
    SANITIZE_DOM: true,
    SANITIZE_NAMED_PROPS: true,
    KEEP_CONTENT: true
  };

  return config;
};

/**
 * Validates and sanitizes PDF annotation URLs using DOMPurify
 * Follows the same security pattern as HTML Viewer
 */
export const validatePDFAnnotationURL = (url: string): URLValidationResult => {
  // Allow internal document references
  if (url.startsWith('#')) {
    return { isValid: true, sanitizedUrl: url };
  }

  // Create a temporary anchor element to test the URL
  const testHTML = `<a href="${url}">test</a>`;
  const config = configureDOMPurifyForPDF();

  try {
    // Use DOMPurify to sanitize the URL
    const sanitized = DOMPurify.sanitize(testHTML, config);

    // Check if DOMPurify kept the link (meaning it's safe)
    if (sanitized.includes('href=')) {
      // Extract the sanitized URL
      const match = sanitized.match(/href="([^"]+)"/);
      if (match && match[1]) {
        return { isValid: true, sanitizedUrl: match[1] };
      }
    }

    return {
      isValid: false,
      reason: 'URL blocked by DOMPurify security policy'
    };
  } catch (error) {
    return {
      isValid: false,
      reason: 'Invalid URL format'
    };
  }
};

/**
 * Sanitizes PDF annotation content using DOMPurify
 * Same approach as HTML Viewer's createMarkup function
 */
export const sanitizePDFAnnotationContent = (content: string): string => {
  const config = configureDOMPurifyForPDF();
  return DOMPurify.sanitize(content, config);
};

/**
 * Log security violations for monitoring
 */
const logSecurityViolation = (violation: SecurityViolation): void => {
  console.warn('PDF Security Violation:', violation);

  // Optional: Send to monitoring service
  // In a real implementation, you might want to send this to your analytics service
  // analyticsService.track('pdf_security_violation', violation);
};

/**
 * Show security warning to user
 */
const showSecurityWarning = (message: string): void => {
  // Use existing notification system or create toast
  console.warn(`PDF Security Warning: ${message}`);

  // Optional: Show user-friendly notification
  // In a real implementation, you might want to use your notification system
  // NotificationService.warning(`Blocked unsafe link: ${message}`);
};

/**
 * Enhanced annotation layer sanitization
 * Processes the entire annotation layer HTML using DOMPurify
 */
export const sanitizeAnnotationLayer = (annotationLayerElement: any): void => {
  const config = configureDOMPurifyForPDF();

  // Get all anchor elements in the annotation layer
  const links = annotationLayerElement.querySelectorAll('a[href]');

  links.forEach((link: any) => {
    const href = link.getAttribute('href');
    if (href) {
      // Create sanitized HTML for this link
      const linkHTML = link.outerHTML;
      const sanitizedHTML = DOMPurify.sanitize(linkHTML, config);

      // If DOMPurify removed the link, replace with span
      if (!sanitizedHTML.includes('href=')) {
        const span = document.createElement('span');
        span.textContent = link.textContent || '';
        span.className = 'blocked-annotation-link';
        span.title = 'Link blocked for security reasons';
        span.style.cursor = 'not-allowed';
        span.style.color = '#da1e28'; // Carbon error color
        span.style.textDecoration = 'line-through';
        link.parentNode?.replaceChild(span, link);

        // Log the security violation
        logSecurityViolation({
          type: 'blocked_url',
          url: href,
          reason: 'URL blocked by DOMPurify during sanitization',
          timestamp: new Date()
        });
      }
    }
  });
};

/**
 * PDF Annotation Event Handler with DOMPurify Integration
 */
export const createSecurePDFAnnotationHandler = () => {
  return (event: any) => {
    // Intercept annotation click events
    if (event.target && event.target.tagName === 'A') {
      event.preventDefault();

      const href = event.target.getAttribute('href');
      if (href) {
        const validation = validatePDFAnnotationURL(href);

        if (validation.isValid && validation.sanitizedUrl) {
          // Safe to navigate - URL has been validated by DOMPurify
          window.open(validation.sanitizedUrl, '_blank', 'noopener,noreferrer');
        } else {
          console.warn(
            `Blocked unsafe PDF annotation link: ${href}`,
            validation.reason
          );
          logSecurityViolation({
            type: 'blocked_url',
            url: href,
            reason: validation.reason || 'Unknown',
            timestamp: new Date()
          });
          // Show user notification
          showSecurityWarning(
            `Link blocked for security: ${validation.reason}`
          );
        }
      }
    }
  };
};

/**
 * Initialize PDF security for annotation layer
 * Call this after the PDF page has loaded and annotation layer is available
 */
export const initializePDFSecurity = (annotationLayerElement?: any): void => {
  const annotationLayer =
    annotationLayerElement || document.querySelector('.annotationLayer');

  if (annotationLayer) {
    // Sanitize the annotation layer
    sanitizeAnnotationLayer(annotationLayer);

    // Add click handler for runtime protection
    const handler = createSecurePDFAnnotationHandler();
    annotationLayer.addEventListener('click', handler);

    // Store handler reference for cleanup
    annotationLayer._securityHandler = handler;
  }
};

/**
 * Cleanup PDF security handlers
 */
export const cleanupPDFSecurity = (annotationLayerElement?: any): void => {
  const annotationLayer =
    annotationLayerElement || document.querySelector('.annotationLayer');

  if (annotationLayer && annotationLayer._securityHandler) {
    annotationLayer.removeEventListener(
      'click',
      annotationLayer._securityHandler
    );
    delete annotationLayer._securityHandler;
  }
};
